import { Injectable } from '@angular/core';
import { enumEntityByKey, enumEntityModelCode } from '@data-layer/enums/entities';
import { EntityModel } from '@data-layer/models/entities';
import { enumIdentifierQualifier } from '@data-layer/enums/common';
import { FormHelper } from '@helpers/form.helper';
import { IsfStateService } from './isf-state.service';

@Injectable({ providedIn: 'root' })
export class IsfPossibleEntitiesService {
  public isfEntityCode: enumEntityModelCode;
  public possibleIsfEntities: EntityModel[] = [];

  constructor(private isfStateService: IsfStateService, private fh: FormHelper) {}

  public calculatePossibleIsfEntities(isfEntityCode: enumEntityModelCode, possibleIsfEntities: EntityModel[]): EntityModel[] {
    if (isfEntityCode === enumEntityModelCode.MANUFACTURER) {
      return this.calculatePossibleIsfManufacturerEntities();
    }

    this.isfEntityCode = isfEntityCode;
    this.possibleIsfEntities = possibleIsfEntities;

    this.updatePossibleIsfEntities();

    return this.possibleIsfEntities;
  }

  public calculatePossibleIsfManufacturerEntities(): EntityModel[] {
    // use seller as manufacturer

    const possibleManufacturers = [];
    const manufacturersList = this.isfStateService.getIsfManufacturerEntityList();
    const seller = this.isfStateService.getIsfEntityById(enumEntityByKey.SELLER);
    if (
      !!seller &&
      this.fh.isValidIsfEntity(seller) &&
      this.checkPossibleIsfEntityBySSNorEIN(seller) &&
      !manufacturersList.find((m) => m.id === seller?.id)
    ) {
      possibleManufacturers.push(new EntityModel({ ...seller, entityCode: enumEntityModelCode.SELLER }));
    }

    return possibleManufacturers;
  }

  public checkPossibleIsfEntityBySSNorEIN(isfEntity: EntityModel): boolean {
    if (
      isfEntity.entityIdentifierQualifier === enumIdentifierQualifier.EIN ||
      isfEntity.entityIdentifierQualifier === enumIdentifierQualifier.SSN
    ) {
      if (!isfEntity.name || !isfEntity.address || !this.fh.isValidIsfEntityAddress(isfEntity.address)) {
        return false;
      }
    }

    return true;
  }

  private updatePossibleIsfEntities(): void {
    // use importer or company
    if ([enumEntityModelCode.CONSIGNEE, enumEntityModelCode.BUYER, enumEntityModelCode.SHIP_TO].indexOf(this.isfEntityCode) !== -1) {
      const importer: EntityModel = this.isfStateService.getIsfEntityById(enumEntityByKey.IMPORTER);
      // The importer will not be inherited automatically for the CN, ST if he is a foreigner.
      //  The user will be able to choose the foreign Importer as a base only for BY.
      // use importer
      if (
        importer &&
        (importer?.entityIdentifierQualifier !== enumIdentifierQualifier.ANI || this.isfEntityCode === enumEntityModelCode.BUYER)
      ) {
        this.possibleIsfEntities.push(new EntityModel({ ...importer }));
      }
      // use company
      else {
        const companyEntityId = 1;
        this.possibleIsfEntities.push(this.isfStateService.initImporterFromCompany(companyEntityId, this.isfEntityCode));
      }
    }
    // use consignee
    if ([enumEntityModelCode.BUYER, enumEntityModelCode.SHIP_TO].indexOf(this.isfEntityCode) !== -1) {
      const importer: EntityModel = this.isfStateService.getIsfEntityById(enumEntityByKey.IMPORTER);
      const consignee: EntityModel = this.isfStateService.getIsfEntityById(enumEntityByKey.CONSIGNEE);

      if (importer?.id !== consignee?.id) {
        this.possibleIsfEntities.push(new EntityModel({ ...consignee, entityCode: enumEntityModelCode.CONSIGNEE }));
      }
    }
    // use buyer
    if ([enumEntityModelCode.SHIP_TO].indexOf(this.isfEntityCode) !== -1) {
      const buyer: EntityModel = this.isfStateService.getIsfEntityById(enumEntityByKey.BUYER);
      if (
        !!buyer &&
        buyer.entityCode !== enumEntityModelCode.IMPORTER &&
        this.fh.isValidIsfEntity(buyer) &&
        this.checkPossibleIsfEntityBySSNorEIN(buyer)
      ) {
        this.possibleIsfEntities.push(new EntityModel({ ...buyer, entityCode: enumEntityModelCode.BUYER }));
      }
    }
    // use seller
    if (
      [enumEntityModelCode.CONSOLIDATOR, enumEntityModelCode.STAFFING_LOCATION, enumEntityModelCode.MANUFACTURER].indexOf(
        this.isfEntityCode,
      ) !== -1
    ) {
      const seller: EntityModel = this.isfStateService.getIsfEntityById(enumEntityByKey.SELLER);
      if (!!seller && this.fh.isValidIsfEntity(seller) && this.checkPossibleIsfEntityBySSNorEIN(seller)) {
        this.possibleIsfEntities.push(new EntityModel({ ...seller, entityCode: enumEntityModelCode.SELLER }));
      }
    }
    // use manufacturers
    if ([enumEntityModelCode.CONSOLIDATOR, enumEntityModelCode.STAFFING_LOCATION].indexOf(this.isfEntityCode) !== -1) {
      const manufacturers: EntityModel[] = this.isfStateService.getIsfManufacturerEntityList();
      manufacturers.forEach((manufacturer: EntityModel) => {
        if (!this.possibleIsfEntities.find((item: EntityModel) => item.id === manufacturer.id)) {
          this.possibleIsfEntities.push(new EntityModel({ ...manufacturer, entityCode: enumEntityModelCode.MANUFACTURER }));
        }
      });
    }
    // use consolidator
    if ([enumEntityModelCode.STAFFING_LOCATION].indexOf(this.isfEntityCode) !== -1) {
      const consolidator: EntityModel = this.isfStateService.getIsfEntityById(enumEntityByKey.CONSOLIDATOR);
      const seller: EntityModel = this.isfStateService.getIsfEntityById(enumEntityByKey.SELLER);

      if (
        !!consolidator &&
        seller?.id !== consolidator.id &&
        consolidator.entityCode !== enumEntityModelCode.STAFFING_LOCATION &&
        this.fh.isValidIsfEntity(consolidator)
      ) {
        this.possibleIsfEntities.push(
          new EntityModel({
            ...consolidator,
            entityCode: enumEntityModelCode.CONSOLIDATOR,
          }),
        );
      }
    }
  }
}
